import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { useCompanyData } from '../../../../../../../../Hooks/TT_ELD/Companies';
import {
  useCompanyEldKey,
  useEldKeys,
  useMigrateToLegal,
  useMigrateToNonLegal,
  useMigrationStatus,
  useRetryMigration,
} from '../../../../../../../../Hooks/TT_ELD/LegalMigration';
import { apiErrorMessage, MigrationDirection } from '../../../../../../../../API/TT_ELD/legalMigration';
import MigrationProgress, { DIRECTION_LABEL } from './MigrationProgress';
import SubscriptionStep from './SubscriptionStep';
import { DEFAULT_ELD, STANDARD_ELD } from '../../../../../../../../Utils/eldTypeLabels';

const { Text, Paragraph } = Typography;

// Who can start migrations / change the price from this tab (the tab itself uses the same roles)
export const ELD_MIGRATION_ROLES = ['manager', 'sales'];

const CONSEQUENCES: Record<MigrationDirection, string[]> = {
  to_legal: [
    `The company switches to ${STANDARD_ELD} immediately.`,
    'Drivers who are Off Duty / Sleeper Berth (and deactivated drivers) are migrated now and logged out.',
    'Drivers who are On Duty / Driving keep working as before and switch automatically the next time they go Off Duty or Sleeper Berth.',
    'The last 8 days of logs are imported in the background as requested edits that drivers accept in the app.',
  ],
  to_non_legal: [
    `The company switches back to ${DEFAULT_ELD} immediately.`,
    'Drivers who are Off Duty / Sleeper Berth (and deactivated drivers) are migrated now and logged out.',
    `Drivers who are On Duty / Driving keep working on ${STANDARD_ELD} and switch automatically the next time they go Off Duty or Sleeper Berth.`,
    'Their Legal logs from the last 8 days are imported in the background as requested logs.',
  ],
};

const StartMigrationModal = ({
  direction,
  companyName,
  loading,
  onConfirm,
  onCancel,
}: {
  direction: MigrationDirection;
  companyName: string;
  loading: boolean;
  onConfirm(): void;
  onCancel(): void;
}) => {
  const [typed, setTyped] = useState('');
  const matches = typed.trim().toLowerCase() === companyName.trim().toLowerCase();

  return (
    <Modal
      open
      title={direction === 'to_legal' ? `Switch this company to ${STANDARD_ELD}?` : `Switch this company back to ${DEFAULT_ELD}?`}
      okText="Start migration"
      okButtonProps={{ danger: true, disabled: !matches }}
      confirmLoading={loading}
      onOk={onConfirm}
      onCancel={onCancel}
      destroyOnHidden
    >
      <ul style={{ paddingLeft: 18 }}>
        {CONSEQUENCES[direction].map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <Paragraph>
        Type the company name <Text strong>{companyName}</Text> to confirm:
      </Paragraph>
      <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={companyName} autoFocus />
    </Modal>
  );
};

interface Props {
  role: string;
}

const EldMigration = ({ role }: Props) => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const canManage = ELD_MIGRATION_ROLES.includes(role);

  const { data: company, isLoading: companyLoading } = useCompanyData(id);
  const { data: companyKey } = useCompanyEldKey(id);
  const { data: status, isLoading: statusLoading } = useMigrationStatus(id);
  const { data: eldKeys, isLoading: keysLoading } = useEldKeys();
  const migrateToLegal = useMigrateToLegal(id);
  const migrateToNonLegal = useMigrateToNonLegal(id);
  const retry = useRetryMigration(id);

  const [eldKeyId, setEldKeyId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<MigrationDirection | null>(null);

  if (companyLoading || statusLoading) return <Spin style={{ display: 'block', margin: 40 }} />;

  const isLegal = company?.config?.eldType === 'custom';
  const running = status?.status === 'running';
  // Step 2 opens only after Step 1: the latest migration must be the one that put the company on
  // its current ELD type (to_legal → Standard ELD, to_non_legal → Default ELD)
  const migrationMatchesType =
    !!status && (status.direction === 'to_legal') === isLegal;
  const companyName: string = company?.name || `Company #${id}`;
  const defaultKey = eldKeys?.find((key) => key.is_default);

  const start = async () => {
    try {
      if (confirming === 'to_legal') await migrateToLegal.mutateAsync(eldKeyId);
      else await migrateToNonLegal.mutateAsync();
      message.success('Migration started');
      setConfirming(null);
      // Allowed products/prices depend on the company type, which just changed
      queryClient.invalidateQueries(`getProducts/${id}`);
      queryClient.invalidateQueries(`getPrice/${id}`);
    } catch (error) {
      message.error(apiErrorMessage(error));
    }
  };

  const onRetry = async () => {
    if (!status) return;
    try {
      await retry.mutateAsync(status.direction);
      message.success('Failed drivers were queued again');
    } catch (error) {
      message.error(apiErrorMessage(error));
    }
  };

  const keyOptions = [
    { value: null as number | null, label: `Default key${defaultKey ? ` (${defaultKey.name})` : ''}` },
    ...(eldKeys ?? [])
      .filter((key) => !key.is_default)
      .map((key) => ({ value: key.id as number | null, label: `${key.name} · ${key.eld_provider}` })),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Descriptions size="small" column={{ xs: 1, sm: 3 }}>
          <Descriptions.Item label="Company type">
            <Tag color={isLegal ? 'blue' : 'default'}>{isLegal ? STANDARD_ELD : DEFAULT_ELD}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="ELD key">
            {isLegal
              ? companyKey?.eldKeyName
                ? `${companyKey.eldKeyName}${companyKey.usesDefault ? ' (default)' : ''}`
                : 'Deployment key'
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Last migration">
            {status ? `${DIRECTION_LABEL[status.direction]} · ${status.status}` : 'Never migrated'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Step 1 · Drivers, vehicles and logs">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {running ? (
            <Alert
              type="info"
              showIcon
              message="A migration is in progress. A new one can start once it has finished and every pending driver has switched."
            />
          ) : !canManage ? null : isLegal ? (
            <Space wrap>
              <Text>This company is on {STANDARD_ELD}. Move it back to {DEFAULT_ELD}:</Text>
              <Button danger icon={<SwapOutlined />} onClick={() => setConfirming('to_non_legal')}>
                Switch to {DEFAULT_ELD}
              </Button>
            </Space>
          ) : (
            <Space wrap>
              <Text>ELD key:</Text>
              <Select
                style={{ minWidth: 260 }}
                loading={keysLoading}
                value={eldKeyId}
                onChange={setEldKeyId}
                options={keyOptions}
              />
              <Button type="primary" icon={<SwapOutlined />} onClick={() => setConfirming('to_legal')}>
                Switch to {STANDARD_ELD}
              </Button>
            </Space>
          )}

          {status ? (
            <MigrationProgress status={status} canManage={canManage} retrying={retry.isLoading} onRetry={onRetry} />
          ) : (
            <Text type="secondary">This company has never been migrated.</Text>
          )}
        </Space>
      </Card>

      <Card title="Step 2 · Subscription price">
        <SubscriptionStep
          companyId={id!}
          company={company}
          isLegal={isLegal}
          importRunning={running}
          canManage={canManage}
          migrationStarted={migrationMatchesType}
        />
      </Card>

      {confirming && (
        <StartMigrationModal
          direction={confirming}
          companyName={companyName}
          loading={migrateToLegal.isLoading || migrateToNonLegal.isLoading}
          onConfirm={start}
          onCancel={() => setConfirming(null)}
        />
      )}
    </Space>
  );
};

export default EldMigration;
